import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch } from "wouter";
import { lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import DashboardLayout from "./components/DashboardLayout";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import TrialExpired from "./pages/TrialExpired";
import { useAuth } from "./_core/hooks/useAuth";
import { DashboardLayoutSkeleton } from "./components/DashboardLayoutSkeleton";

// Lazy-loaded dashboard pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const AiChat = lazy(() => import("@/pages/AiChat"));
const Settings = lazy(() => import("@/pages/Settings"));
const FunnelThermometer = lazy(() => import("@/pages/FunnelThermometer"));
const CommandCenter = lazy(() => import("@/pages/CommandCenter"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const Sales = lazy(() => import("@/pages/Sales"));
const Reports = lazy(() => import("@/pages/Reports"));
const Leads = lazy(() => import("@/pages/Leads"));

// Lazy-loaded standalone pages (no trpc/convex)
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const isAppDomain = () => {
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("app.") || host === "localhost";
};

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  // Not logged in
  if (!user) {
    // On app.manyflows.ai → redirect to login (not landing page)
    if (isAppDomain()) {
      window.location.href = "/login";
      return <DashboardLayoutSkeleton />;
    }
    // On manyflows.ai → show landing page
    return (
      <Suspense fallback={<DashboardLayoutSkeleton />}>
        <LandingPage />
      </Suspense>
    );
  }

  // Onboarding not completed — show onboarding flow
  if (!user.onboardingCompleted) {
    return <Onboarding />;
  }

  // Trial expired and not admin/active — show trial expired
  if (user.isTrialExpired && !user.hasAccess) {
    return <TrialExpired />;
  }

  // All good — show dashboard
  return <>{children}</>;
}

function DashboardRoutes() {
  return (
    <AuthGate>
      <DashboardLayout>
        <Suspense fallback={<DashboardLayoutSkeleton />}>
          <Switch>
            <Route path="/" component={Dashboard} />
            <Route path="/campaigns" component={Campaigns} />
            <Route path="/funnel" component={FunnelThermometer} />
            <Route path="/command" component={CommandCenter} />
            <Route path="/chat" component={AiChat} />
            <Route path="/integrations" component={Integrations} />
            <Route path="/sales" component={Sales} />
            <Route path="/leads" component={Leads} />
            <Route path="/reports" component={Reports} />
            <Route path="/settings" component={Settings} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </DashboardLayout>
    </AuthGate>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster
            position="top-right"
            closeButton
            toastOptions={{
              className: "!rounded-xl !shadow-2xl !shadow-black/30",
              style: {
                fontSize: "13px",
              },
            }}
            visibleToasts={4}
            gap={8}
            offset={16}
          />
          <Switch>
            <Route path="/login" component={Login} />
            <Route>
              <DashboardRoutes />
            </Route>
          </Switch>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
